<template>
  <div class="legacy-portfolio-select">
    <button ref="trigger" type="button" class="legacy-portfolio-trigger" :disabled="unavailable"
      aria-haspopup="dialog" :aria-expanded="open" :aria-label="`Seleccionar cartera: ${selectedLabel}`" @click="show">
      <span class="legacy-portfolio-copy">
        <span class="legacy-portfolio-label">Cartera</span>
        <span class="legacy-portfolio-name" :title="selectedLabel">{{ selectedLabel }}</span>
        <span v-if="summary" class="legacy-portfolio-summary"><span v-for="part in summary.split(' · ')" :key="part">{{ part }}</span></span>
      </span>
      <span class="material-icons-outlined" aria-hidden="true">expand_more</span>
    </button>
    <LegacyPortfolioOverlay :open="open" class="legacy-ticker-dialog" @click.self="close" @keydown="onKeydown">
      <section ref="dialog" class="legacy-portfolio-dialog modal-container" role="dialog" aria-modal="true" aria-label="Seleccionar cartera" tabindex="-1">
        <header class="modal-header">
          <div class="title-wrapper">
            <span class="material-icons-outlined header-icon" aria-hidden="true">account_balance_wallet</span>
            <h2 class="modal-title">Seleccionar cartera</h2>
          </div>
          <button type="button" class="legacy-portfolio-close modal-close-btn" aria-label="Cerrar selector de cartera" @click="close">
            <span class="material-icons-outlined" aria-hidden="true">close</span>
          </button>
        </header>
        <div class="modal-body">
          <p v-if="loading" class="portfolio-status" role="status">Actualizando datos de las carteras…</p>
          <div v-else-if="error" class="portfolio-error" role="alert">
            <p>{{ error }} Los datos mostrados corresponden a la última carga.</p>
            <button type="button" class="action-btn" @click="$emit('refresh')">Reintentar</button>
          </div>
          <div class="list-container">
            <button v-for="option in options" :key="option.id" type="button" class="legacy-portfolio-option list-item clickable-item" :class="{ 'is-selected': isSelected(option) }"
              :data-portfolio-id="option.id" :aria-pressed="isSelected(option)" @click="choose(option)">
              <span class="material-icons-outlined portfolio-icon" aria-hidden="true">account_balance_wallet</span>
              <span class="legacy-portfolio-option-copy">
                <span class="legacy-portfolio-option-name item-text" :title="option.label">{{ option.label }}</span>
                <span v-if="option.details && option.details.length" class="portfolio-metrics">
                  <span v-for="detail in option.details" :key="detail.key" class="portfolio-metric" :title="detail.title">
                    <span class="portfolio-metric-label">{{ detail.label }}</span>
                    <span class="portfolio-metric-value">{{ detail.value }}</span>
                  </span>
                </span>
              </span>
              <span v-if="isSelected(option)" class="material-icons-outlined" aria-hidden="true">check</span>
            </button>
          </div>
        </div>
      </section>
    </LegacyPortfolioOverlay>
  </div>
</template>

<script>
import LegacyPortfolioOverlay from './LegacyPortfolioOverlay.vue';
export default {
  name: 'LegacyPortfolioSelect',
  components: { LegacyPortfolioOverlay },
  props: {
    value: { type: [String, Number], default: '' },
    options: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    summary: { type: String, default: '' }
  },
  data: () => ({ open: false }),
  computed: {
    unavailable() { return this.disabled || !this.options.length; },
    selectedLabel() {
      const selected = this.options.find(this.isSelected);
      return selected ? selected.label : (this.unavailable ? 'No disponible' : 'Seleccionar cartera');
    }
  },
  watch: { unavailable(value) { if (value && this.open) this.close(); } },
  methods: {
    isSelected(option) { return String(option.id) === String(this.value); },
    show() {
      if (this.unavailable) return;
      this.open = true;
      this.$emit('open');
      this.$nextTick(() => {
        const dialog = this.$refs.dialog;
        if (dialog) (dialog.querySelector('[aria-pressed=true]') || dialog).focus();
      });
    },
    close() {
      this.open = false;
      this.$nextTick(() => { if (this.$refs.trigger && !this.unavailable) this.$refs.trigger.focus(); });
    },
    choose(option) {
      if (this.unavailable) return;
      if (!this.isSelected(option)) this.$emit('change', String(option.id));
      this.close();
    },
    onKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); }
      if (event.key !== 'Tab') return;
      const dialog = this.$refs.dialog;
      const buttons = Array.from(dialog.querySelectorAll('button:not(:disabled)'));
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { event.preventDefault(); first.focus(); }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../styles/legacy-ticker-dialogs.scss";
.legacy-portfolio-select { width: 100%; height: 100%; min-width: 0; font: inherit; }
.legacy-portfolio-trigger { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; height: 100%; padding: 0 12px; border: 0; border-radius: 0; background: transparent; color: var(--legacy-list-text); font: inherit; text-align: left; cursor: pointer; }
.legacy-portfolio-trigger:hover:not(:disabled) { background: var(--legacy-list-hover); }
.legacy-portfolio-trigger:disabled { opacity: .55; cursor: not-allowed; }
.legacy-portfolio-copy { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.legacy-portfolio-label { font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--legacy-control-muted); }
.legacy-portfolio-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.legacy-portfolio-summary { font-size: 10px; line-height: 1.35; color: var(--legacy-control-muted); overflow-wrap: anywhere; }
.legacy-portfolio-summary > span { display: block; }
.legacy-portfolio-trigger > .material-icons-outlined { font-size: 20px; flex-shrink: 0; }
.legacy-portfolio-trigger:focus-visible { outline: 2px solid var(--workspace-selected-edge); outline-offset: -3px; }
.legacy-ticker-dialog {
  .modal-container { outline: none; max-width: 560px; }
  .modal-header { gap: 16px; }
  .legacy-portfolio-option { align-items: flex-start; gap: 10px; width: 100%; padding: 12px; border: 0; background: transparent; color: inherit; text-align: left; }
  .legacy-portfolio-option-copy { flex: 1; min-width: 0; }
  .legacy-portfolio-option-name { display: block; margin-bottom: 8px; }
  .legacy-portfolio-option > .material-icons-outlined { flex-shrink: 0; font-size: 20px; }
  .portfolio-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px 16px; }
  .portfolio-metric { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .portfolio-metric-label { color: var(--dialog-muted); font-size: 11px; line-height: 1.4; }
  .portfolio-metric-value { font-size: 13px; font-weight: 500; line-height: 1.4; overflow-wrap: anywhere; }
  .portfolio-status { margin: 0 0 12px; font-size: 13px; color: var(--dialog-muted); }
  .portfolio-error { margin-bottom: 16px; font-size: 13px; color: var(--dialog-danger); }
  .portfolio-error p { margin: 0 0 8px; }
  .portfolio-icon { color: var(--dialog-muted); }
  .is-selected .portfolio-icon { color: var(--dialog-selected-text); }
  @media (max-width: 480px) {
    .portfolio-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
}
</style>
